import { createSelector } from '@reduxjs/toolkit'
import * as R from 'ramda'

import type { ISectionTreeNode } from 'components/sections/ProjectStructure/utils'
import { listToTree } from 'components/sections/ProjectStructure/utils'
import { BlockModeEnum, PassOrderEnum } from 'gql/__generated__/graphql'
import type { IProjectContext, Block, Section } from 'services/Store/Project/types'
import { notEmpty } from 'utils/notEmpty'

import { getBlocks, getProjectPassOrder, getSection, getSections, getSectionState } from '..'
import { SectionTypeEnum } from '../../enums'
import { getBlocksState } from './block'

type ISectionTypes = SectionTypeEnum.test | SectionTypeEnum.landing

export const sectionTypesGuard = (type: SectionTypeEnum): type is ISectionTypes => true

const findSection = (
  nodes: ISectionTreeNode<Section>[],
  id: string,
): ISectionTreeNode<Section> | undefined => {
  for (const node of nodes) {
    if (node.item.id === id) {
      return node
    }

    if (node.children) {
      const result = findSection(node.children, id)
      if (result) {
        return result
      }
    }
  }
  return
}

const flattenChapter = (nodes: ISectionTreeNode<Section>[]): ISectionTreeNode<Section>[] => {
  return nodes.flatMap((node) => {
    if (node.item.type === SectionTypeEnum.chapter && node.children) {
      return flattenChapter(node.children)
    }

    if (node.item.type === SectionTypeEnum.cover) {
      return []
    } else {
      return node
    }
  })
}

export type SectionViewState = {
  count: number
  viewed: number
}

export const getViewProgressNoMandatory = createSelector(
  [
    (state: IProjectContext, sectionId: string) => getMandatoryViewableBlocksIds(state, sectionId),
    (state: IProjectContext, sectionId: string) => getAtListOfOneViewed(state, sectionId),
  ],
  (viewableMandatoryBlocksIds, atListOfOneViewed) => {
    // if all blocks is not mandatory and at least of one block is viewed
    if (R.isEmpty(viewableMandatoryBlocksIds) && atListOfOneViewed) {
      return {
        count: 1,
        viewed: 1,
      }
    }
    return null
  },
)

export const getTestViewState = createSelector(
  [
    (state: IProjectContext, sectionId: string) => getSectionState(state, { id: sectionId }),
    (state: IProjectContext) => getBlocksState(state),
    (state: IProjectContext, sectionId: string) => getMandatoryViewableBlocksIds(state, sectionId),
  ],
  (sectionState, blockStates, viewableMandatoryBlocksIds) => {
    let viewed = 0
    if (sectionState?.status === 'inProgress') {
      viewed = R.sum(viewableMandatoryBlocksIds.map((id) => (blockStates[id]?.result ? 1 : 0)))
    } else if (sectionState?.status === 'finished') {
      viewed = viewableMandatoryBlocksIds.length
    }
    return { count: viewableMandatoryBlocksIds.length, viewed }
  },
)

export const getLandingsViewState = createSelector(
  [
    (state: IProjectContext) => getProjectPassOrder(state),
    (state: IProjectContext) => getBlocksState(state),
    (state: IProjectContext, sectionId: string) => getMandatoryViewableBlocksIds(state, sectionId),
  ],
  (passOrder, blockStates, viewableMandatoryBlocksIds) => {
    const isLinerCourseProcedure = passOrder === PassOrderEnum.linear
    const viewedSecCountMandatory = R.sum(
      viewableMandatoryBlocksIds.map((id) => {
        const blockIsViewed = blockStates[id]?.viewed
        if (isLinerCourseProcedure) {
          return blockIsViewed && !blockStates[id]?.incomplete ? 1 : 0
        } else {
          return blockIsViewed ? 1 : 0
        }
      }),
    )
    return {
      count: viewableMandatoryBlocksIds.length,
      viewed: viewedSecCountMandatory,
    }
  },
)

const SECTIONS_MAPPING = {
  [SectionTypeEnum.test]: getTestViewState,
  [SectionTypeEnum.landing]: getLandingsViewState,
}

export const getSectionViewState = createSelector(
  [
    (state: IProjectContext, sectionId: string) => getViewProgressNoMandatory(state, sectionId),
    (state: IProjectContext, sectionId: string, sectType: ISectionTypes) =>
      SECTIONS_MAPPING[sectType](state, sectionId),
  ],
  (viewProgressIfNoMandatory, viewState) => viewProgressIfNoMandatory || viewState,
)

export const getSectionsFlat = createSelector(
  [
    (_state: IProjectContext, sectionId?: string) => sectionId,
    (state: IProjectContext) => getSections(state),
  ],
  (sectionId, sections) => {
    const tree = listToTree(sections)
    const sectionsSubTree = (sectionId ? [findSection(tree, sectionId)] : tree).filter(notEmpty)
    return flattenChapter(sectionsSubTree)
  },
)

export const getViewStatesData = (viewStates: SectionViewState[]) => {
  return viewStates.reduce(
    (acc: SectionViewState, item: SectionViewState) => ({
      count: acc.count + item.count,
      viewed: acc.viewed + item.viewed,
    }),
    { count: 0, viewed: 0 },
  )
}

export const getProgress = createSelector(
  [
    (state: IProjectContext, sectionId?: string) => {
      const sectionsFlat = getSectionsFlat(state, sectionId)
      return sectionsFlat.map((section: ISectionTreeNode<Section>) => {
        const sectionType = section.item.type
        if (!sectionTypesGuard(sectionType)) {
          throw new Error('Unknown section type')
        }
        return getSectionViewState(state, section.item.id, sectionType)
      })
    },
  ],
  (viewStates) => {
    const { viewed, count } = getViewStatesData(viewStates)
    return (viewed / (count || 1)) * 100
  },
)

export const getContinueBlockId = createSelector(
  [
    (state: IProjectContext, sectionId?: string) => getBlocksState(state, { sectionId }),
    (state: IProjectContext, sectionId?: string) =>
      getSection(state, { id: sectionId })?.blocksOrder || [],
  ],
  (blockState, order) => {
    return R.findLast<string>((id) => Boolean(blockState[id]?.viewed), order) || R.head(order)
  },
)

export const getViewableBlocksIds = createSelector(
  [(state: IProjectContext, sectionId?: string) => getBlocks(state, sectionId)],
  (blocks) => R.compose(getBlocksUuids, filterViewableBlocks)(blocks),
)

export const getContinueTestQuestionId = createSelector(
  [
    (state: IProjectContext, sectionId?: string) => getBlocksState(state, { sectionId }),
    (state: IProjectContext, sectionId?: string) =>
      getSection(state, { id: sectionId })?.blocksOrder || [],
  ],
  (blockState, order) =>
    R.findLast<string>((id) => Boolean(blockState[id]?.isReady), order) || R.last(order),
)

export const getMandatoryViewableBlocksIds = createSelector(
  [(state: IProjectContext, sectionId?: string) => getBlocks(state, sectionId)],
  (blocks) => R.compose(getBlocksUuids, filterViewableBlocks, filterMandatoryBlocks)(blocks),
)

export const getAtListOfOneViewed = createSelector(
  [
    (state: IProjectContext, sectionId: string) => getBlocksState(state, { sectionId }),
    (state: IProjectContext, sectionId: string) => getViewableBlocksIds(state, sectionId),
  ],
  (blockStates, viewedBlocksIds) => viewedBlocksIds.some((id) => blockStates[id]?.viewed),
)
const getBlocksUuids = (blocks: Block[]) => R.map<Block, string>(R.prop('uuid'), blocks)

const filterMandatoryBlocks = (blocks: Block[]) =>
  R.filter(
    R.propSatisfies((isMandatory) => isMandatory, 'isMandatory'),
    blocks,
  )

const filterViewableBlocks = (blocks: Block[]) =>
  R.filter(
    R.propSatisfies((mode) => VIEWABLE_BLOCK_MODES.includes(mode), 'mode'),
    blocks,
  )

const VIEWABLE_BLOCK_MODES = [BlockModeEnum.questions, BlockModeEnum.view]
